import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const { logout, getAuthHeader, user } = useAuth();
  const navigate = useNavigate();

  // Add user form
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'capo_evento'
  });

  // Change password form
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: getAuthHeader()
      });
      alert('Utente creato con successo!');
      setShowAddModal(false);
      setNewUser({ username: '', email: '', password: '', role: 'capo_evento' });
      fetchUsers();
    } catch (error) {
      console.error('Errore creazione utente:', error);
      alert(error.response?.data?.detail || 'Errore durante la creazione dell\'utente');
    }
  };

  const handleDeleteUser = async (username) => {
    try {
      await axios.delete(`${API}/admin/users/${username}`, {
        headers: getAuthHeader()
      });
      alert('Utente eliminato con successo!');
      setShowDeleteModal(null);
      fetchUsers();
    } catch (error) {
      console.error('Errore eliminazione utente:', error);
      alert(error.response?.data?.detail || 'Errore durante l\'eliminazione dell\'utente');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users/change-password`, {
        username: showPasswordModal,
        new_password: newPassword
      }, {
        headers: getAuthHeader()
      });
      alert('Password modificata con successo!');
      setShowPasswordModal(null);
      setNewPassword('');
    } catch (error) {
      console.error('Errore cambio password:', error);
      alert(error.response?.data?.detail || 'Errore durante il cambio password');
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center space-x-3">
              <h1 className="text-base font-semibold text-gray-900">BETTI SRL</h1>
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-gray-500">Amministrazione</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-xs text-gray-600">{user?.username}</span>
              <button
                onClick={() => navigate('/settings')}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                Impostazioni
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                Dashboard
              </button>
              <button
                onClick={logout}
                className="text-xs text-gray-500 hover:text-gray-900"
                data-testid="logout-btn"
              >
                Esci
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Gestione Utenti</h2>
            <p className="text-sm text-gray-600 mt-1">Amministra gli accessi all'applicazione</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800"
            data-testid="add-user-btn"
          >
            + Aggiungi Utente
          </button>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">Nessun utente trovato</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ruolo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creato il</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((usr) => (
                  <tr key={usr.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{usr.username}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{usr.email}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs font-medium ${
                        usr.role === 'admin' ? 'bg-red-100 text-red-700' :
                        usr.role === 'capo_evento' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {usr.role === 'admin' ? 'Amministratore' :
                         usr.role === 'capo_evento' ? 'Capo Evento' :
                         'Visualizzatore'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(usr.created_at)}</td>
                    <td className="px-6 py-4 text-right text-sm space-x-2">
                      <button
                        onClick={() => setShowPasswordModal(usr.username)}
                        className="text-gray-700 hover:text-gray-900"
                        data-testid={`change-password-${usr.username}`}
                      >
                        Cambia Password
                      </button>
                      {usr.username !== user?.username && (
                        <button
                          onClick={() => setShowDeleteModal(usr.username)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`delete-user-${usr.username}`}
                        >
                          Elimina
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-md w-full p-6 border border-gray-300">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Aggiungi Nuovo Utente</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                  required
                  data-testid="new-username-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                  required
                  data-testid="new-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                  required
                  minLength="6"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ruolo</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                  data-testid="new-role-select"
                >
                  <option value="visualizzatore">Visualizzatore (solo lettura)</option>
                  <option value="capo_evento">Capo Evento (crea/modifica report)</option>
                  <option value="admin">Amministratore (controllo totale)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {newUser.role === 'admin' && '• Può gestire utenti e report'}
                  {newUser.role === 'capo_evento' && '• Può creare, modificare ed eliminare report'}
                  {newUser.role === 'visualizzatore' && '• Può solo visualizzare i report'}
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-gray-900 text-white hover:bg-gray-800"
                  data-testid="submit-new-user-btn"
                >
                  Crea Utente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-md w-full p-6 border border-gray-300">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Conferma Eliminazione</h3>
            <p className="text-sm text-gray-600 mb-6">
              Eliminare l'utente "<span className="font-semibold">{showDeleteModal}</span>"?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900"
              >
                Annulla
              </button>
              <button
                onClick={() => handleDeleteUser(showDeleteModal)}
                className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-user-btn"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-md w-full p-6 border border-gray-300">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Cambia Password</h3>
            <p className="text-sm text-gray-600 mb-4">Utente: <span className="font-semibold">{showPasswordModal}</span></p>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nuova Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
                  required
                  minLength="6"
                  data-testid="change-password-input"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(null);
                    setNewPassword('');
                  }}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:border-gray-900"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-gray-900 text-white hover:bg-gray-800"
                  data-testid="submit-password-change-btn"
                >
                  Cambia Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
